import { useState, useEffect } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faLightbulb, faHeart, faArrowRight, faCamera, faVideo } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useFamily } from '../../contexts/FamilyContext';

export default function DebriefPhase({ experienceId, experience }) {
  const { user } = useAuth();
  const { family } = useFamily();
  const [debrief, setDebrief] = useState(experience.debrief || {});
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!experienceId) return;
    setIsSaving(true);
    try {
      await updateDoc(doc(db, 'projects', experienceId), {
        debrief: {
          ...debrief,
          updatedAt: serverTimestamp(),
          updatedBy: user?.uid
        }
      });
      toast.success('Debrief saved');
    } catch (error) {
      console.error('Error saving debrief:', error);
      toast.error('Failed to save debrief');
    } finally {
      setIsSaving(false);
    }
  };

  const updateDebriefField = (field, value) => {
    setDebrief(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="max-w-4xl mx-auto animate-fadeIn">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-text-primary mb-3">Experience Debrief</h1>
        <p className="text-lg text-text-secondary">
          Reflect on what happened, what you learned, and how to improve next time.
        </p>
      </div>

      {/* Overall Rating */}
      <div className="bg-white rounded-2xl border-2 border-border p-6 mb-6">
        <h2 className="text-xl font-bold text-text-primary mb-4 flex items-center gap-2">
          <FontAwesomeIcon icon={faStar} className="text-primary" />
          Overall Experience
        </h2>
        <div className="flex gap-2 mb-4">
          {[1, 2, 3, 4, 5].map((rating) => (
            <button
              key={rating}
              onClick={() => updateDebriefField('overallRating', rating)}
              className={`w-12 h-12 rounded-lg border-2 transition-all ${
                debrief.overallRating === rating
                  ? 'bg-primary border-primary text-white'
                  : 'border-border hover:border-primary'
              }`}
            >
              {rating}
            </button>
          ))}
        </div>
        {debrief.overallRating && (
          <p className="text-sm text-text-secondary">
            {debrief.overallRating === 5 && 'Excellent! Everything went perfectly.'}
            {debrief.overallRating === 4 && 'Great! Mostly went well.'}
            {debrief.overallRating === 3 && 'Good. Some things could be improved.'}
            {debrief.overallRating === 2 && 'Okay. Several things need work.'}
            {debrief.overallRating === 1 && 'Needs improvement. Significant changes needed.'}
          </p>
        )}
      </div>

      {/* What Went Well */}
      <div className="bg-white rounded-2xl border-2 border-border p-6 mb-6">
        <h2 className="text-xl font-bold text-text-primary mb-4 flex items-center gap-2">
          <FontAwesomeIcon icon={faHeart} className="text-accent" />
          What Went Well
        </h2>
        <textarea
          value={debrief.whatWentWell || ''}
          onChange={(e) => updateDebriefField('whatWentWell', e.target.value)}
          placeholder="What moments, decisions, or elements made this experience special?"
          className="w-full min-h-[120px] p-4 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary resize-none"
        />
      </div>

      {/* What Could Be Improved */}
      <div className="bg-white rounded-2xl border-2 border-border p-6 mb-6">
        <h2 className="text-xl font-bold text-text-primary mb-4 flex items-center gap-2">
          <FontAwesomeIcon icon={faLightbulb} className="text-primary" />
          What Could Be Improved
        </h2>
        <textarea
          value={debrief.whatCouldImprove || ''}
          onChange={(e) => updateDebriefField('whatCouldImprove', e.target.value)}
          placeholder="What would you do differently next time? What challenges did you face?"
          className="w-full min-h-[120px] p-4 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary resize-none"
        />
      </div>

      {/* Key Learnings */}
      <div className="bg-white rounded-2xl border-2 border-border p-6 mb-6">
        <h2 className="text-xl font-bold text-text-primary mb-4">Key Learnings</h2>
        <textarea
          value={debrief.keyLearnings || ''}
          onChange={(e) => updateDebriefField('keyLearnings', e.target.value)}
          placeholder="What did you learn about yourself, your family, or Jewish practice through this experience?"
          className="w-full min-h-[120px] p-4 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary resize-none"
        />
      </div>

      {/* Memorable Moments */}
      <div className="bg-white rounded-2xl border-2 border-border p-6 mb-6">
        <h2 className="text-xl font-bold text-text-primary mb-4">Memorable Moments</h2>
        <textarea
          value={debrief.memorableMoments || ''}
          onChange={(e) => updateDebriefField('memorableMoments', e.target.value)}
          placeholder="Share specific moments, conversations, or experiences that stood out..."
          className="w-full min-h-[120px] p-4 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary resize-none"
        />
      </div>

      {/* Photos/Videos */}
      <div className="bg-white rounded-2xl border-2 border-border p-6 mb-6">
        <h2 className="text-xl font-bold text-text-primary mb-4">Photos & Videos</h2>
        <div className="space-y-4">
          {debrief.media?.map((item, index) => (
            <div key={index} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
              <div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center">
                <FontAwesomeIcon 
                  icon={item.type === 'image' ? faCamera : faVideo} 
                  className="text-2xl text-primary"
                />
              </div>
              <div className="flex-1">
                <p className="font-medium text-text-primary">{item.caption || 'Untitled'}</p>
                <p className="text-sm text-text-secondary">{item.url}</p>
              </div>
            </div>
          ))}
          <button className="w-full p-4 border-2 border-dashed border-border rounded-lg hover:border-primary transition-colors text-text-secondary">
            + Add Photo or Video
          </button>
        </div>
      </div>

      {/* Notes for Next Time */}
      <div className="bg-white rounded-2xl border-2 border-border p-6 mb-6">
        <h2 className="text-xl font-bold text-text-primary mb-4">Notes for Next Time</h2>
        <textarea
          value={debrief.notesForNextTime || ''}
          onChange={(e) => updateDebriefField('notesForNextTime', e.target.value)}
          placeholder="Any specific notes, reminders, or ideas to remember for the next time you plan this experience..."
          className="w-full min-h-[120px] p-4 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary resize-none"
        />
      </div>

      {/* Save Button */}
      <div className="flex justify-end gap-4">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isSaving ? 'Saving...' : 'Save Debrief'}
          {!isSaving && <FontAwesomeIcon icon={faArrowRight} />}
        </button>
      </div>
    </div>
  );
}
